import axios from "axios";
import {
  type IBookingCreditCardData,
  type IbookingPayment,
  type IBookingRoom,
  type IBookingState,
} from "./bookingInterface";
import { type IHotelState } from "../hotel/hotelInterface";
import { getOccupancyData } from "../../helpers/occupancy";

/**
 * Create the reservation
 * @param Hotel
 * @param Booking
 * @returns Object
 */
export function createReservationGroup(
  Hotel: IHotelState,
  Booking: IBookingState,
  CreditCardData: IBookingCreditCardData | null
) {
  // Format telephone number
  let Customer = { ...Booking.Customer };
  Customer.Telephone = Booking.Customer?.Telephone.replace("-", "");

  // Add notes to the reservation
  let Reservations: any = [];
  try {
    Booking.Reservations.forEach((reservation, i) => {
      let item = { ...reservation };
      Reservations[i] = Object.assign({}, item, {
        Notes: Customer.Notes,
        OccupancyData:
          item.OccupancyData ??
          getOccupancyData(
            item.RoomCategoryId,
            item.AdultCount,
            item.ChildCount,
          ),
      });
    });
  } catch (error) {
    console.log("Error adding notes", error);
  }

  let data: any = {
    Client: import.meta.env.VITE_MEWS_API_CLIENT,
    ConfigurationId: Hotel.ConfigurationId,
    HotelId: Hotel.Id,
    Customer: Customer,
    Reservations: Reservations,
  };

  if (CreditCardData !== null) {
    data["CreditCardData"] = CreditCardData;
  }
  return axios({
    method: "post",
    url: import.meta.env.VITE_MEWS_API_URL + "reservationGroups/create",
    data,
  });
}

export function getReservationGroup(Hotel: IHotelState, Booking: IBookingState) {
  return axios({
    method: "post",
    url: import.meta.env.VITE_MEWS_API_URL + "reservationGroups/get",
    data: {
      Client: import.meta.env.VITE_MEWS_API_CLIENT,
      HotelId: Hotel.Id,
      ReservationGroupId: Booking.Payment.ReservationGroupId,
      Extent: { PaymentRequests: true, Payments: true },
    },
  });
}

export function getReservationPrice(hotel: IHotelState, booking: IBookingState, reservations?: IBookingRoom[]) {
  const source = reservations ?? booking.Reservations;
  const payload = source
    .filter((reservation) => reservation.RateId && reservation.StartUtc && reservation.EndUtc)
    .map((reservation, index) => ({
      Identifier: `reservation-${index}`,
      RoomCategoryId: reservation.RoomCategoryId,
      RateId: reservation.RateId,
      StartUtc: reservation.StartUtc,
      EndUtc: reservation.EndUtc,
      AdultCount: reservation.AdultCount,
      ChildCount: reservation.ChildCount,
      ProductIds: reservation.ProductIds ?? [],
      VoucherCode: booking.VoucherCode,
      OccupancyData:
        reservation.OccupancyData ??
        getOccupancyData(
          reservation.RoomCategoryId,
          reservation.AdultCount,
          reservation.ChildCount,
        ),
    }));

  if (!payload.length) {
    return Promise.resolve({ data: { ReservationPrice: [] } });
  }

  return axios({
    method: "post",
    url: import.meta.env.VITE_MEWS_API_URL + "reservations/price",
    data: {
      Client: import.meta.env.VITE_MEWS_API_CLIENT,
      ConfigurationId: hotel.ConfigurationId,
      CurrencyCode: hotel.Culture.CurrencyCode,
      Reservations: payload,
    },
  });
}

export function getPaymentCardsAll(PaymentCardId: IbookingPayment["PaymentCardId"]) {
  return axios({
    method: "post",
    url: import.meta.env.VITE_MEWS_API_URL + "paymentCards/getAll",
    data: {
      Client: import.meta.env.VITE_MEWS_API_CLIENT,
      PaymentCardIds: [PaymentCardId],
    },
  });
}

export function getPaymentCardsAuthorize(
  hotel: IHotelState,
  PaymentCardId: IbookingPayment["PaymentCardId"]
) {
  const date = new Date();
  return axios({
    method: "post",
    url: import.meta.env.VITE_MEWS_API_URL + "paymentCards/authorize",
    data: {
      Client: import.meta.env.VITE_MEWS_API_CLIENT,
      EnterpriseId: hotel.EnterpriseId,
      PaymentCardId: PaymentCardId,
      BrowserInfo: {
        ScreenWidth: window.screen.width,
        ScreenHeight: window.screen.height,
        ColorDepth: window.screen.colorDepth,
        UserAgent: navigator.userAgent,
        Language: navigator.language,
        JavaEnabled: false,
        TimeZoneOffset: date.getTimezoneOffset(),
      },
    },
  });
}

export function validateVoucherCode(hotel: IHotelState, VoucherCode: IBookingState["VoucherCode"]) {
  return axios({
    method: "post",
    url: import.meta.env.VITE_MEWS_API_URL + "vouchers/validate",
    data: {
      Client: import.meta.env.VITE_MEWS_API_CLIENT,
      HotelId: hotel.Id,
      VoucherCode,
    },
  });
}
